import React from 'react';
import { TFunction, useTranslation } from 'next-i18next';
import { AttachedFileProps } from '../../Contact/utils/upload.types';
import Loader from '../../Loader';

import {
  ProgressBar,
  ProgressValue,
  StyledCheckSvg,
  StyledCloseSvg,
  UploadedFile,
  UploadedFileContent,
  UploadedFileName,
  UploadStatusMessage,
  UploadWrapper,
} from './styled';

export type AttachmentProps = {
  fileName: string;
  status: AttachedFileProps['status'];
  percentage: number;
  id: string;
  onRemove?: (id: string) => void;
};

const getStatusMessage = ({
  status,
  percentage,
  t,
}: {
  status: AttachedFileProps['status'];
  percentage: number;
  t: TFunction;
}) => {
  switch (status) {
    case 'uploading':
      return (
        <>
          <Loader />
          <span>{t('uploading')}</span>
          <span>- {percentage}%</span>
        </>
      );
    case 'uploaded':
      return (
        <>
          <StyledCheckSvg />
          <span>{t('uploaded')}</span>
          <span>- 100%</span>
        </>
      );
    case 'error':
      return <span>{t('uploadFailed')}</span>;
    default:
      return '';
  }
};

const Attachment = ({
  fileName,
  status,
  percentage,
  id,
  onRemove,
}: AttachmentProps) => {
  const { t } = useTranslation();
  const showProgressBar = status === 'uploading' || status === 'uploaded';

  return (
    <UploadWrapper isError={status === 'error'}>
      <UploadedFile>
        <UploadedFileContent>
          <UploadedFileName>{fileName}</UploadedFileName>
          <UploadStatusMessage status={status}>
            {getStatusMessage({ status, percentage, t })}
          </UploadStatusMessage>
        </UploadedFileContent>
        <StyledCloseSvg
          onClick={() => {
            onRemove?.(id);
          }}
        />
      </UploadedFile>
      {showProgressBar && (
        <ProgressBar>
          <ProgressValue percentage={percentage} />
        </ProgressBar>
      )}
    </UploadWrapper>
  );
};

export default Attachment;
